import { type ReactNode } from 'react';
import { cn } from '@/lib/utils';

interface ChatColumnProps {
  /**
   * When true, wraps the centered column in an outer gutter row that
   * adds `px-[var(--chat-col-gutter)]` so content doesn't touch the
   * viewport edges on narrow screens. Use for full-bleed wrappers
   * (chat input dock, docked cards). Leave false when an ancestor
   * already provides horizontal padding (e.g. the message scroll
   * region).
   */
  padded?: boolean;
  /** Applied to the outermost element. */
  className?: string;
  /** Applied to the inner (centered, width-capped) element. Only meaningful when `padded`. */
  innerClassName?: string;
  children?: ReactNode;
}

/**
 * The single source of truth for "this content lives in the chat
 * column": it pulls width from `--chat-col-width` and (optionally)
 * horizontal padding from `--chat-col-gutter`. Use everywhere the
 * chat input, message list, banners, and docked cards align —
 * adding a new component that lives in the column should mean
 * reaching for this, not re-typing the CSS-variable references.
 */
export const ChatColumn = ({
  padded = false,
  className,
  innerClassName,
  children,
}: ChatColumnProps) => {
  if (padded) {
    return (
      <div className={cn('w-full px-[var(--chat-col-gutter)]', className)}>
        <div className={cn('mx-auto w-full max-w-[var(--chat-col-width)]', innerClassName)}>
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className={cn('mx-auto w-full max-w-[var(--chat-col-width)]', className)}>
      {children}
    </div>
  );
};
